import React, {useState} from 'react';
import {Alert, Button, Form, Input, message, Modal, Space, Tag, Tooltip} from 'antd';
import {BulbOutlined, CloseOutlined, PlusOutlined, SaveOutlined} from '@ant-design/icons';
import {createWordData, suggestWord} from '../api/wordDataApi';

const { TextArea, Search } = Input;

function WordDataCreateModal({ isOpen, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const handleSubmit = async (values) => {
    setCreateError(null);
    setCreateLoading(true);
    try {
      await createWordData(values);
      form.resetFields();
      setSuggestions([]);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setCreateError('등록에 실패했습니다: ' + (err?.response?.data?.message || err.message));
    } finally {
      setCreateLoading(false);
    }
  };

  const applySuggestion = (s) => {
    if (!s) return;
    const patch = {};
    if (s.wordEngNm) patch.wordEngNm = s.wordEngNm;
    if (s.wordExpln) patch.wordExpln = s.wordExpln;
    if (s.wordEngAbbrNm) patch.wordEngAbbrNm = s.wordEngAbbrNm;
    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch);
    }
  };

  const handleAiSuggest = async () => {
    try {
      if (aiLoading) return; // 연타 방지
      const wordNm = form.getFieldValue('wordNm');
      const domainClsfNm = form.getFieldValue('domainClsfNm');
      if (!wordNm) {
        message.warning('먼저 단어명을 입력해주세요.');
        return;
      }
      setAiLoading(true);
      const data = await suggestWord({ wordNm, domainClsfNm });
      const arr = Array.isArray(data) ? data : (data ? [data] : []);
      const top5 = arr.slice(0, 5);
      setSuggestions(top5);
      if (top5.length === 0) {
        message.info('AI가 추천할 내용을 찾지 못했습니다.');
      } else {
        message.success(`${top5.length}건의 추천을 가져왔습니다. 태그를 클릭해 적용하세요.`);
      }
    } catch (err) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setAiLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSuggestions([]);
    setCreateError(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined />
          <span>단어 신규생성</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      width={800}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          wordNm: '',
          wordEngAbbrNm: '',
          wordEngNm: '',
          wordExpln: '',
          domainClsfNm: '',
          synm: '',
          phwrd: '',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item label="단어명" name="wordNm" rules={[{ required: true, message: '단어명을 입력해주세요' }]}>
            <Search
              placeholder="단어명을 입력하세요"
              allowClear
              enterButton={
                <Button type="primary" icon={<BulbOutlined />} loading={aiLoading} htmlType="button">
                  AI추천
                </Button>
              }
              onSearch={handleAiSuggest}
            />
          </Form.Item>

          <Form.Item label="도메인" name="domainClsfNm">
            <Input placeholder="도메인을 입력하세요" />
          </Form.Item>

          {/* AI 추천 태그 (전체 폭) - 상단 버튼 제거, 제목 유지 */}
          <div style={{ gridColumn: '1 / span 2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: '#555' }}>AI 추천</div>
              <div />
            </div>
            {suggestions.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {suggestions.map((s, idx) => {
                  const baseWordNm = (form.getFieldValue('wordNm') || '').toString().trim();
                  const abbr = (s.wordEngAbbrNm || '-').toString();
                  const eng = (s.wordEngNm || '-').toString();
                  const expl = (s.wordExpln || '-').toString();
                  const label = `${baseWordNm || '-'} | ${abbr} | ${eng} | ${expl}`;
                  return (
                    <Tooltip
                      key={idx}
                      placement="top"
                      title={
                        <div>
                          <div><strong>영문명:</strong> {eng}</div>
                          <div><strong>설명:</strong> {expl}</div>
                        </div>
                      }
                    >
                      <Tag color="blue" onClick={() => applySuggestion(s)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        {label}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>

          <Form.Item label="영문약어" name="wordEngAbbrNm" rules={[{ required: true, message: '영문약어를 입력해주세요' }]}>
            <Input placeholder="영문약어를 입력하세요" />
          </Form.Item>

          <Form.Item label="영문명" name="wordEngNm" rules={[{ required: true, message: '영문명을 입력해주세요' }]}>
            <Input placeholder="영문명을 입력하세요" />
          </Form.Item>

          <Form.Item label="설명" name="wordExpln" rules={[{ required: true, message: '설명을 입력해주세요' }]} style={{ gridColumn: '1 / span 2' }}>
            <TextArea rows={3} placeholder="설명을 입력하세요" />
          </Form.Item>

          <Form.Item label="동의어" name="synm">
            <Input placeholder="동의어를 입력하세요" />
          </Form.Item>

          <Form.Item label="금칙어" name="phwrd">
            <Input placeholder="금칙어를 입력하세요" />
          </Form.Item>
        </div>

        {createError && (
          <Alert message="오류" description={createError} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Button icon={<CloseOutlined />} onClick={handleClose} style={{ marginRight: 8 }}>
            취소
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={createLoading}>
            저장
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default WordDataCreateModal;
